<template>
	<div
		v-if="product && product.vanr"
		ref="el"
		class="isolate shadow-md border border-border-tertiary rounded-2.5xl relative flex flex-col pt-4"
		:class="{'hover:border-border-highlighted transition duration-300': isInStock(product) !== false}"
	>
		<ProductRibbons :product="product" class="absolute left-2 top-2 z-10 overflow-hidden" />
		<div class="relative">
			<ProductImage
				:product="product"
				variant="thumbnail"
				loading="lazy"
				class="w-full rem:h-[190px] object-contain block px-5"
			/>
			<ProductBadges
				v-if="product.badges"
				:product="product"
				:badges="product.badges"
				:age-limitation="product.ageLimitation"
				class="absolute inset-x-5 inset-y-0"
			/>
			<ProductOutOfStockBar :product="product" />
		</div>
		<div class="mt-4">
			<h3
				class="typo-h5 text-center text-heading-secondary line-clamp-2 mb-2 px-2 sm:px-5"
			>
				<AbsoluteInternalLink
					:to="branchSpecificPath({
						name: route.path.includes('cela-nabidka') || linkToProductCatalog
							? 'hypermarket-cela-nabidka-category-p-product'
							: 'hypermarket-akcni-nabidka-category-p-product',
						params: {product: createHouseProductSlug(product)},
					})"
					class="before:cover-parent focus-outline"
					:aria-label="`Zobrazit ${product.name}`"
				>
					{{ product.name }}
				</AbsoluteInternalLink>
			</h3>

			<div v-if="product.productInHouse" class="text-neutral-700 text-xs flex justify-center md:gap-2 flex-col md:flex-row py-1 max-md:items-center">
				<span v-if="product.productInHouse.comparisonPrice && product.productInHouse.comparisonSaleUnitSizeText">
					{{ $n(product.productInHouse.comparisonPrice, 'currencyWithPennies') }}/
					{{ removeDecimalComma(product.productInHouse.comparisonSaleUnitSizeText) }}
					<span v-if="product.productInHouse.bonusProgramPrice" class="md:hidden" aria-hidden="true">|</span>
				</span>
				<span v-if="product.productInHouse.bonusProgramPrice && product.productInHouse.bonusProgramPrice.comparisonPrice && product.productInHouse.bonusProgramPrice.comparisonSaleUnitSizeText" class="max-md:hidden" aria-hidden="true">|</span>
				<span v-if="product.productInHouse.bonusProgramPrice && product.productInHouse.bonusProgramPrice.comparisonPrice && product.productInHouse.bonusProgramPrice.comparisonSaleUnitSizeText" class="flex items-center gap-1">
					<NuxtIcon name="custom/heart" class="text-sm" />
					{{ $n(product.productInHouse.bonusProgramPrice.comparisonPrice, 'currencyWithPennies') }}/
					{{ removeDecimalComma(product.productInHouse.bonusProgramPrice.comparisonSaleUnitSizeText) }}
				</span>
			</div>
		</div>
		<div
			class="flex gap-1 items-end justify-between px-2 sm:px-5 py-2 sm:py-4 relative bg-white rounded-b-2.5xl z-20 mt-auto mb-0"
		>
			<div class="self-center">
				<Transition
					mode="out-in"
					enter-from-class="translate-y-[30px] opacity-0"
					leave-to-class="translate-y-[30px] opacity-0"
				>
					<div v-if="!isShoppingListOpen" class="transition-all duration-300">
						<ProductTriplePricing
							v-if="product.productInHouse"
							:product-in-house="product.productInHouse"
						/>
					</div>
					<span
						v-else-if="shoppingLists?.totalCount && shoppingLists.totalCount > 4"
						class="bg-white shadow-sm rounded-full typo-body-caption text-body-secondary px-4 py-2 font-bold mb-1 block"
					>
						{{ $t('plural.shoppingLists', shoppingLists?.totalCount - 4) }}
					</span>
				</Transition>

				<span v-if="product.productInHouse?.priceValidTo && visiblePriceValidTo(product.productInHouse)" class="text-xs text-neutral-800 block">
					Platné do: {{ format(product.productInHouse.priceValidTo, 'd.M.') }}
				</span>
			</div>
			<ProductShoppingListControl
				v-model:is-shopping-list-open="isShoppingListOpen"
				class="max-sm:flex-col rounded-full"
				:class="isAuthenticated && 'bg-background-secondary p-0.5'"
				:product="product"
				:embedded-picker="false"
				method="product_cart"
			/>
			<span class="sr-only" aria-live="polite" role="status" aria-atomic="true">
				{{ ariaStatusMessage }}
			</span>
		</div>
		<Transition
			mode="out-in"
			@enter="animateShoppingListsIn"
			@leave="animateShoppingListsOut"
			@after-enter="shoppingListFocusTrap.activate()"
			@after-leave="shoppingListFocusTrap.deactivate()"
		>
			<div v-if="isShoppingListOpen" ref="shoppingListWrapper" class="absolute inset-0 rounded-2.5xl overflow-hidden">
				<div
					class="absolute z-10 bg-white rem:bottom-[50px] sm:rem:bottom-[80px] top-0 left-0 right-0 flex flex-col"
					data-shopping-list-container
				>
					<div
						class="flex flex-col sm:flex-row px-5 pt-4 pb-3 gap-2 sm:gap-4 border-b border-light-600"
					>
						<div v-if="product.imgThumbnail" class="relative rem:h-[70px] rem:w-[70px] shrink-0">
							<AbsoluteInternalLink
								:to="branchSpecificPath({
									name: route.path.includes('cela-nabidka') || linkToProductCatalog
										? 'hypermarket-cela-nabidka-category-p-product'
										: 'hypermarket-akcni-nabidka-category-p-product',
									params: {product: createHouseProductSlug(product)},
								})"
							>
								<ProductImage
									:product="product"
									variant="thumbnail"
									loading="lazy"
									class="w-full h-full object-contain"
								/>
							</AbsoluteInternalLink>
						</div>
						<div class="flex-1">
							<AbsoluteInternalLink
								:to="branchSpecificPath({
									name: route.path.includes('cela-nabidka') || linkToProductCatalog
										? 'hypermarket-cela-nabidka-category-p-product'
										: 'hypermarket-akcni-nabidka-category-p-product',
									params: {product: createHouseProductSlug(product)},
								})"
								class="text-xs font-semibold text-neutral-600 leading-[1.15rem] mb-2 line-clamp-2"
							>
								{{ product.name }}
							</AbsoluteInternalLink>
							<div class="flex items-center justify-end gap-2">
								<ProductTriplePricing
									v-if="product.productInHouse"
									:product-in-house="product.productInHouse"
								/>
							</div>
						</div>
					</div>
					<div class="overflow-y-scroll grow">
						<ProductShoppingListPicker @select="isShoppingListOpen = false" />
					</div>
				</div>
			</div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
	import {format} from 'date-fns';
	import {gsap} from 'gsap';
	import {storeToRefs} from 'pinia';
	import {useFocusTrap} from '@vueuse/integrations/useFocusTrap';
	import {ThisAndThat_CollectEvent as ThisAndThatCollectEvent, ThisAndThatService, type Gsoa_ProductCatalog_ProductDetail as ProductDetail} from '~/openapi';
	import {useShoppingListsStore} from '~/stores/shoppingLists';
	import {removeDecimalComma} from '~/utils/products';
	import {isInStock} from '~/utils';
	import {shoppingListsBusKey} from '~/buses/shoppingLists';
	import {createHouseProductSlug} from '~/utils/createHouseProductSlug';

	interface Props {
		product: ProductDetail | undefined;
		mimiclub?: boolean;
		productCategory?: string;
		isPending?: boolean;
		linkToProductCatalog?: boolean;
		isThisAndThatProduct?: boolean;
	}

	const props = defineProps<Props>();
	const {product} = props;

	const route = useRoute();
	const shoppingListWrapper = useTemplateRef<HTMLElement>('shoppingListWrapper');
	const isShoppingListOpen = ref<boolean>(false);
	const el = ref<HTMLElement | null>(null);
	const ariaStatusMessage = useAriaStatusMessage();
	const branchSpecificPath = useBranchSpecificPath();

	const shoppingListFocusTrap = useFocusTrap(shoppingListWrapper, {
		allowOutsideClick: true,
		clickOutsideDeactivates: (event: MouseEvent | TouchEvent) => {
			const target = event.target;
			if (!(target instanceof Element)) {
				return true;
			}

			return !target.closest('[data-shopping-list-toggle]');
		},
		onDeactivate() {
			isShoppingListOpen.value = false;
		},
	});

	const shoppingListsStore = useShoppingListsStore();
	const {shoppingLists} = storeToRefs(shoppingListsStore);

	const {isAuthenticated} = useAuth();

	if (import.meta.client) {
		const shoppingListsBus = useEventBus(shoppingListsBusKey);

		const unsubscribeEventBus = shoppingListsBus.on((e) => {
			if (e.productId !== product?.vanr) {
				return;
			}

			switch (e.type) {
				case 'add':
					ariaStatusMessage.value = 'Produkt byl přidán do nákupního seznamu.';
					break;

				case 'remove':
					ariaStatusMessage.value = 'Produkt byl odstraněn z nákupního seznamu.';
					break;
			}
		});

		onUnmounted(() => {
			unsubscribeEventBus();
		});
	}

	watch(() => props.isPending, initObserving, {immediate: true});

	function initObserving() {
		const recommendation = product?.thisAndThat;
		if (props.isPending || !recommendation || !props.isThisAndThatProduct) {
			return;
		}

		const {stop} = useIntersectionObserver(el, ([{isIntersecting}]) => {
			if (!isIntersecting) {
				return;
			}

			const productInHouse = product.productInHouse;
			const bonusProgramPrice = productInHouse?.bonusProgramPrice;
			const isBonusPrice = typeof bonusProgramPrice?.actualPrice === 'number';
			const price = isBonusPrice ? bonusProgramPrice.actualPrice : productInHouse?.actualPrice;
			const originalPrice = isBonusPrice ? bonusProgramPrice.originalPrice : productInHouse?.originalPrice;

			if (!product.vanr || !productInHouse?.houseNumber || typeof price !== 'number') {
				stop();
				return;
			}

			ThisAndThatService.thisAndThatCollectEvent({
				requestBody: {
					productId: product.vanr,
					marketId: productInHouse.houseNumber,
					eventType: ThisAndThatCollectEvent.eventType.SEEN,
					origin: recommendation.origin,
					price,
					position: recommendation.position,
					originId: recommendation.originId,
					originalPrice,
					priceType: productInHouse.priceType,
					strategy: recommendation.strategy,
				},
			}).catch((error) => {
				if (import.meta.dev) {
					console.warn('[Tracking] Failed to log "seen" event:', error);
				}
			});

			stop();
		});
	}

	function animateShoppingListsIn(el: Element, onComplete: () => void) {
		const container = el.querySelector('[data-shopping-list-container]');
		gsap.fromTo(container, {yPercent: 100}, {yPercent: 0, duration: 0.3, onComplete});
	}

	function animateShoppingListsOut(el: Element, onComplete: () => void) {
		const container = el.querySelector('[data-shopping-list-container]');
		gsap.to(container, {yPercent: 100, duration: 0.3, onComplete});
	}
</script>
